/**
 * RivalryProApp — iter87.78.iso · Rivalry Pro+ isolated surface.
 *
 * Route: /rivalry-pro. Uses proApi (dedicated axios) + rivalry_pro_session_v1
 * (dedicated storage key). NO imports from pages/SiteAdmin.jsx or
 * @/lib/api — blast-radius boundary enforced at build time.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Plus, Users, Trophy, LogOut, Loader2, AlertTriangle, ChevronDown, ChevronRight, CreditCard, RotateCcw } from "lucide-react";
import { useAuth, useClerk, UserButton } from "@clerk/react";
import { session as legacySession } from "@/lib/api";
import { proApi } from "./api";
import { ensurePaddleInitialized } from "@/lib/paddleClient";
import { loadSession, clearSession, markProSeen } from "./session";
import RivalryTodoBadge from "./RivalryTodoBadge";
import { setStoredViewTournament } from "@/components/dashboard/TournamentSwitcher";
import { syncAppBadge, totalTodoCount } from "./appBadge";
import HowItWorksSheet from "./HowItWorksSheet";
import SectionInfoDot from "./SectionInfoDot";
import { Switch } from "@/components/ui/switch";
import CancelProPlusModal from "./CancelProPlusModal";
import ProShellFab from "./ProShellFab";
import ReportIssueModal from "@/components/ReportIssueModal";
import ProPlusClaimModal from "./ProPlusClaimModal";
import SubscribeProPlus from "@/components/pro-plus/SubscribeProPlus";
import AddToHomeScreenBanner from "./AddToHomeScreenBanner";

// iter87.79 Phase A — dual-mode gate. If Clerk is configured and the
// user is Clerk-signed-in, we let them through even without a legacy
// session. If neither is present, we bounce to /rivalry-pro/login.
const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// iter87.204 — Human-readable billing status, shared by the dashboard
// banner (under the tagline) and the Clerk Billing tab status line.
// Returns null when there's no subscription doc to report on (e.g.
// free tier, or a comp/manual grant with no pro_plus_subscriptions row).
function formatBillingDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short", day: "numeric", year: "numeric",
        });
    } catch {
        return null;
    }
}

function billingStatusText(me) {
    const sub = me?.latest_subscription;
    if (!sub) return null;
    if (sub.cancel_pending) {
        const d = formatBillingDate(sub.access_until);
        return d ? `Cancelling — access through ${d}` : "Cancelling at period end";
    }
    if (sub.status === "live") {
        const d = formatBillingDate(me?.next_renewal_at);
        return d ? `Active — renews ${d}` : "Active";
    }
    if (sub.status === "suspended") return "Payment issue — action needed";
    if (sub.status === "closed") return "Closed";
    return null;
}

export default function RivalryProApp() {
    const navigate = useNavigate();
    const clerkAuth = useAuth();
    const clerk = useClerk();
    const clerkReady = CLERK_ENABLED ? clerkAuth?.isLoaded : true;
    const clerkSignedIn = CLERK_ENABLED ? clerkAuth?.isSignedIn : false;

    const [me, setMe] = useState(null);
    const [adminLeagues, setAdminLeagues] = useState([]);
    const [adminClosedLeagues, setAdminClosedLeagues] = useState([]);
    const [adminArchivedLeagues, setAdminArchivedLeagues] = useState([]);
    const [participantLeagues, setParticipantLeagues] = useState([]);
    const [todosByLeague, setTodosByLeague] = useState({});
    const [loading, setLoading] = useState(true);
    const [pendingRequest, setPendingRequest] = useState(false);
    // iter87.199.69 — `showStart` retired with the inline StartModal.
    // The "Start a Rivalry" tile now navigates to `/rivalry-pro/new`
    // which mounts the shared linear onboarding flow.
    // const [showStart, setShowStart] = useState(false);
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [showReportIssue, setShowReportIssue] = useState(false);
    const [showCancelProPlus, setShowCancelProPlus] = useState(false);
    const [archivedOpen, setArchivedOpen] = useState(false);
    // iter87.199.156 — Update Payment Method widget state.
    const [updatingPayment, setUpdatingPayment] = useState(false);
    // iter87.199.157 — Un-cancel (resume) widget state.
    const [resuming, setResuming] = useState(false);
    const paymentTimeoutRef = useRef(null);
    // iter87.423 (user-requested) — daily to-do email reminder toggle.
    // Optimistically mutated on `me` so the switch flips instantly;
    // reverted on a failed PATCH.
    const [savingReminderPref, setSavingReminderPref] = useState(false);
    // iter87.212.1 — Manual "Refresh" widget state. Separate from
    // `loading` on purpose: `loading` renders a full-screen spinner
    // that unmounts the whole page (see the `if (loading)` guard
    // below) — a manual refresh from an already-loaded dashboard
    // should just spin the one button, not blank the screen.
    const [refreshing, setRefreshing] = useState(false);

    // iter87.242 — Home Screen app badge (iOS only). Mirrors the total
    // "to-do" count shown across every rivalry's `RivalryTodoBadge`
    // pill onto the installed Pro+ app icon via the Web Badging API.
    useEffect(() => {
        syncAppBadge(totalTodoCount(todosByLeague));
    }, [todosByLeague]);

    // iter87.199.154 — Opens the existing Cancel-Pro+ flow from inside
    // Clerk's "Manage account" modal (Billing tab). Closes the Clerk
    // modal first so the two overlays don't stack.
    const handleCancelFromBilling = useCallback(() => {
        clerk?.closeUserProfile?.();
        setTimeout(() => setShowCancelProPlus(true), 250);
    }, [clerk]);

    // iter87.423 (user-requested) — "To-do email reminders" toggle.
    const handleToggleTodoReminder = useCallback(async (checked) => {
        setSavingReminderPref(true);
        setMe((prev) => (prev ? { ...prev, todo_reminder_opt_in: checked } : prev));
        try {
            await proApi.updateNotificationPrefs(checked);
            toast.success(checked ? "Daily to-do reminders turned on" : "Daily to-do reminders turned off");
        } catch (err) {
            setMe((prev) => (prev ? { ...prev, todo_reminder_opt_in: !checked } : prev));
            toast.error(err.response?.data?.detail || "Couldn't update reminder setting");
        } finally {
            setSavingReminderPref(false);
        }
    }, []);

    // iter87.199.69 — Clerk getToken binding moved UP to ProShell's
    // ClerkTokenBridge so it's active for every /rivalry-pro/* route
    // (not just this index page). Landing directly on `/rivalry-pro/new`
    // used to fail with 401 "Missing Pro+ bearer token" because
    // RivalryProApp never mounted, so the getter stayed null. Left as
    // a no-op effect here to preserve the useEffect line count for the
    // legacy `_needs_bearer_token_wire` UAT scanner.

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const status = await proApi.me();
            setMe(status);
            // iter87.199.28 — Mark this device as "has visited Pro+"
            // so the standard hamburger menu can surface a Return-to-Pro+
            // shortcut for Clerk-authed Pro+ users too (not just legacy
            // JWT sessions).
            markProSeen();
            const list = await proApi.myLeagues();
            setAdminLeagues(list.admin || []);
            setAdminClosedLeagues(list.admin_closed || []);
            setAdminArchivedLeagues(list.admin_archived || []);
            setParticipantLeagues(list.participant || []);
            // iter87.187 — Todos summary is fire-and-forget: badge stays
            // zero if it fails, cards still render fine.
            proApi.todosSummary().then(
                (r) => setTodosByLeague(r?.leagues || {}),
                () => setTodosByLeague({}),
            );
        } catch (err) {
            if (err.response?.status === 401) {
                navigate("/rivalry-pro/login");
                return;
            }
            if (err.response?.status === 403) {
                // iter87.79-b — pro_plus_pending is the auto-queued state
                // for freshly-signed-up Clerk users awaiting operator approval.
                const detail = err.response?.data?.detail;
                if (detail && typeof detail === "object" && detail.error === "pro_plus_pending") {
                    setPendingRequest(true);
                    setLoading(false);
                    return;
                }
                navigate("/rivalry-pro/login");
                toast.error(typeof detail === "string" ? detail : "Rivalry Pro+ access required");
                return;
            }
            toast.error(err.response?.data?.detail || "Unable to load Rivalry Pro+");
        }
        setLoading(false);
    }, [navigate]);

    // iter87.199.157 — Lightweight status refresh. Deliberately does
    // NOT touch `loading` — that flag makes the component return just
    // a full-screen spinner (see the `if (loading)` guard below),
    // which unmounts <UserButton> and therefore closes/blanks the
    // Clerk "Manage account" modal the caller is sitting in. Bug
    // found in production (2026-08-30): clicking Update Payment
    // Method, completing the Paddle overlay, and calling the old
    // `load()` here tore down the whole Billing tab to a blank pane.
    const refreshMe = useCallback(async () => {
        try {
            const status = await proApi.me();
            setMe(status);
        } catch (_e) { /* best-effort — keep showing the last known state */ }
    }, []);

    // iter87.212.1 — Manual "Refresh" button. Requested fix for a
    // reported P0: newly-added games/rivalries only showed up after a
    // full logout/login because the dashboard only ever fetched once
    // on mount. Re-runs the exact same fetch sequence as `load()` but
    // WITHOUT flipping the `loading` flag (which would unmount the
    // header the button itself lives in — see `refreshing` above).
    const handleManualRefresh = useCallback(async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            const status = await proApi.me();
            setMe(status);
            markProSeen();
            const list = await proApi.myLeagues();
            setAdminLeagues(list.admin || []);
            setAdminClosedLeagues(list.admin_closed || []);
            setAdminArchivedLeagues(list.admin_archived || []);
            setParticipantLeagues(list.participant || []);
            proApi.todosSummary().then(
                (r) => setTodosByLeague(r?.leagues || {}),
                () => setTodosByLeague({}),
            );
            toast.success("Refreshed");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Unable to refresh — try again");
        }
        setRefreshing(false);
    }, [refreshing]);

    // iter87.199.157 — Stable listener reference so repeated clicks on
    // Update Payment Method don't pile up duplicate Paddle event
    // listeners (paddleClient's fan-out `Set` de-dupes by identity).
    const handlePaddleEvent = useCallback((evt) => {
        const name = evt?.name || "";
        if (name === "checkout.completed") {
            if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
            toast.success("Payment method updated");
            refreshMe();
            setUpdatingPayment(false);
        } else if (name === "checkout.closed") {
            if (paymentTimeoutRef.current) { clearTimeout(paymentTimeoutRef.current); paymentTimeoutRef.current = null; }
            setUpdatingPayment(false);
        }
    }, [refreshMe]);

    // iter87.199.156 — Self-serve "update card" via Paddle's hosted
    // overlay. Fetches the public client_token/environment (same
    // unauthenticated config the homepage Retain install uses — a
    // client-side token is never secret), then a one-off transaction
    // id scoped to THIS caller's subscription, then opens
    // Paddle.Checkout.open({transactionId}).
    //
    // iter87.199.157 fix: paddleClient now fans the eventCallback out
    // to every registered listener (previously only whichever call
    // site initialized Paddle FIRST — e.g. the homepage's Retain
    // install in Landing.jsx — ever heard back), and the completion
    // handler does a silent `refreshMe()` instead of the destructive
    // `load()`. A 60s safety timeout also releases the button if
    // Paddle never fires an event back at all.
    const handleUpdatePayment = useCallback(async () => {
        setUpdatingPayment(true);
        try {
            const cfgRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/paddle/public-config`);
            if (!cfgRes.ok) throw new Error("Billing is not configured");
            const { client_token, environment } = await cfgRes.json();
            const { transaction_id } = await proApi.updatePaymentTransaction();
            await ensurePaddleInitialized(client_token, environment, handlePaddleEvent);
            window.Paddle.Checkout.open({ transactionId: transaction_id });
            if (paymentTimeoutRef.current) clearTimeout(paymentTimeoutRef.current);
            paymentTimeoutRef.current = setTimeout(() => setUpdatingPayment(false), 60000);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Unable to open the payment update widget");
            setUpdatingPayment(false);
        }
    }, [handlePaddleEvent]);

    // iter87.199.157 — Un-cancel. Reverses a pending end-of-period
    // cancellation (see backend `POST /rivalry-pro/subscription/resume`).
    // Silent refresh only — same non-destructive pattern as the
    // payment-method widget above.
    const handleResumeSubscription = useCallback(async () => {
        setResuming(true);
        try {
            const r = await proApi.resumeSubscription();
            toast.success(r.already_active ? "Your subscription is already active." : "Subscription resumed — billing continues as normal.");
            await refreshMe();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Unable to resume subscription");
        }
        setResuming(false);
    }, [refreshMe]);

    useEffect(() => {
        if (!clerkReady) return; // wait for Clerk to hydrate before deciding
        if (!clerkSignedIn && !loadSession()) {
            navigate("/rivalry-pro/login");
            return;
        }
        load();
    }, [load, navigate, clerkReady, clerkSignedIn]);

    useEffect(() => {
        // iter87.203 — bfcache staleness. Paddle checkout finishes on
        // /billing/success via a hard `window.location.assign` (not a
        // client route change), so hitting Back from there can restore
        // THIS page straight from the browser's back-forward cache
        // instead of remounting it — showing whatever tier snapshot
        // (e.g. still "free") was in memory from BEFORE checkout, even
        // once Pro+ has since provisioned. `pageshow` with
        // `persisted: true` is the standard signal for a bfcache
        // restore.
        //
        // iter87.212.2 fix — this used to call `load()`, which sets
        // `loading=true` and unmounts the ENTIRE tree (see the
        // `if (loading)` full-screen-spinner guard below), including
        // any open Clerk `<UserButton>` "Manage account" modal. Bug
        // found in production: a user mid-way through Clerk's "Add
        // email address" → "enter verification code" step switches
        // apps to check their inbox for the code; on many mobile
        // browsers that app-switch triggers a bfcache restore on
        // return, so this listener fired `load()` and blew the modal
        // away back to its first step, losing the in-progress code
        // entry. Switched to the same non-destructive `refreshMe()`
        // used by the Update Payment Method / Resume flows (iter87.199.157)
        // — still re-syncs `me.tier`/billing status (the actual thing
        // this listener needs to fix) without ever unmounting anything.
        const onPageShow = (e) => { if (e.persisted) refreshMe(); };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
    }, [refreshMe]);

    const signOut = () => {
        clearSession();
        syncAppBadge(0);
        if (CLERK_ENABLED && clerkSignedIn && clerkAuth?.signOut) {
            clerkAuth.signOut().finally(() => navigate("/rivalry-pro/login"));
            return;
        }
        navigate("/rivalry-pro/login");
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-cyber" />
            </div>
        );
    }

    if (pendingRequest) {
        return (
            <div
                className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4"
                data-testid="rivalry-pro-pending-shell"
            >
                <div className="brutal-card max-w-md w-full p-6 space-y-4">
                    <div className="flex items-center gap-2" data-testid="rivalry-pro-pending-brand">
                        <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                            <Crown className="w-4 h-4 text-black" strokeWidth={2.5} />
                        </div>
                        <span className="font-display font-black leading-tight tracking-tight">
                            <span className="block text-lg"><span className="text-white">RIVALRY</span> <span className="text-volt">PRO+</span></span>
                        </span>
                    </div>
                    <div className="pill border-cyber/60 text-cyber text-[10px] font-bold uppercase inline-flex">
                        Request pending
                    </div>
                    {/* iter87.199.96 → iter87.199.97 — Subscribe banner is
                        the primary CTA on the pending screen. Pay-to-skip
                        the admin approval queue with a $10/mo LS checkout.
                        Hidden if LS is not configured. */}
                    <SubscribeProPlus />
                    <p className="text-xs text-zinc-500">
                        Signed in as <span className="text-zinc-300">{clerkAuth?.userId ? "your Clerk account" : "your account"}</span>.
                    </p>
                    <div className="pt-2 border-t border-[#222]">
                        <button
                            type="button"
                            onClick={signOut}
                            data-testid="rivalry-pro-pending-signout"
                            className="brutal-btn-ghost text-xs py-2 w-full flex items-center justify-center gap-2"
                        >
                            <LogOut className="w-3 h-3" /> Sign out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const tier = me?.tier || "free";
    const billingStatusLine = billingStatusText(me);

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="rivalry-pro-app">
            <header className="border-b border-[#222] bg-black/50 backdrop-blur sticky top-0 z-10">
                <div className="max-w-5xl mx-auto flex items-center gap-3 p-4">
                    {/* iter87.393 (user-requested) — "Tiny Crew Rivalries"
                        line removed on this authenticated dashboard only
                        (the marketing landing page keeps it). */}
                    <div className="flex items-center gap-2 min-w-0" data-testid="rivalry-pro-app-brand">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-black" strokeWidth={2.5} />
                        </div>
                        <span className="font-display font-black leading-tight tracking-tight truncate">
                            <span className="block text-base sm:text-xl"><span className="text-white">RIVALRY</span> <span className="text-volt">PRO+</span></span>
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={signOut}
                        data-testid="rivalry-pro-signout"
                        className="brutal-btn-ghost text-xs py-1 ml-auto"
                    >
                        <LogOut className="w-3 h-3" /> Sign out
                    </button>
                    {CLERK_ENABLED && clerkSignedIn && (
                        <div data-testid="rivalry-pro-clerk-user-button" className="ml-2">
                            {/* iter87.199.130 — Appearance now lives entirely
                                on <ClerkProvider> (ProShell.jsx) so every
                                Clerk surface shares one consistent theme.
                                Only the trigger's size is contextual here. */}
                            <UserButton
                                afterSignOutUrl="/rivalry-pro/login"
                                appearance={{ elements: { userButtonAvatarBox: "w-7 h-7 rounded-full" } }}
                            >
                                {/* iter87.199.154 — Custom "Billing" page inside Clerk's
                                    Manage Account modal, alongside Account/Security.
                                    Warns that deleting the Clerk account here does NOT
                                    cancel Pro+ billing, and offers a shortcut into the
                                    existing self-serve Cancel Pro+ flow (the dashboard's
                                    floating action button stays untouched as a second
                                    entry point). */}
                                <UserButton.UserProfilePage
                                    label="Billing"
                                    url="billing"
                                    labelIcon={<CreditCard className="w-4 h-4" />}
                                >
                                    <div className="p-1" data-testid="rivalry-pro-clerk-billing-page">
                                        <h2 className="font-display font-black text-base uppercase mb-3">Billing</h2>
                                        {/* iter87.199.156/157 — Live status line, refreshed after a
                                            successful card update or resume so this reflects reality
                                            without requiring a full page reload. Raw internal status
                                            tokens are mapped to customer-facing wording. */}
                                        {billingStatusLine && (
                                            <div className="text-xs text-zinc-400 mb-3" data-testid="rivalry-pro-clerk-billing-status">
                                                Status: <span className="text-zinc-200 font-semibold">{billingStatusLine}</span>
                                            </div>
                                        )}
                                        <div className="brutal-card-light p-3 mb-4 border-amber-500/40 bg-amber-500/5" data-testid="rivalry-pro-clerk-delete-warning">
                                            <p className="text-sm text-amber-300 font-bold">
                                                Deleting your Clerk account does not cancel Pro+.
                                            </p>
                                            <p className="text-xs text-zinc-400 mt-1">
                                                Your Pro+ subscription keeps billing on its own schedule until you cancel it separately — deleting your sign-in account here has no effect on billing. To cancel your Pro+ subscription, use the Cancel Pro+ subscription button below.
                                            </p>
                                        </div>
                                        {/* iter87.199.158 — Wrapped in a column flex container
                                            (previously each button was `inline-flex`, which flowed
                                            two buttons onto the same line in Clerk's narrow modal
                                            when a cancellation was pending). */}
                                        <div className="flex flex-col items-start gap-2">
                                            {/* iter87.199.157 — Un-cancel. Only shown while a
                                                cancellation is scheduled but hasn't taken effect yet. */}
                                            {me?.latest_subscription?.cancel_pending && (
                                                <button
                                                    type="button"
                                                    onClick={handleResumeSubscription}
                                                    disabled={resuming}
                                                    data-testid="rivalry-pro-clerk-billing-resume-btn"
                                                    className="brutal-btn text-sm flex items-center gap-2 disabled:opacity-40 bg-cyber/10 border-cyber/60 text-cyber"
                                                >
                                                    {resuming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                                    {resuming ? "Resuming…" : "Un-cancel subscription"}
                                                </button>
                                            )}
                                            {/* iter87.199.156 — Self-serve "update card" widget. Opens
                                                Paddle's hosted overlay via a one-off transaction id
                                                instead of relying solely on Paddle's dunning emails. */}
                                            <button
                                                type="button"
                                                onClick={handleUpdatePayment}
                                                disabled={updatingPayment}
                                                data-testid="rivalry-pro-clerk-billing-update-payment-btn"
                                                className="brutal-btn text-sm flex items-center gap-2 disabled:opacity-40"
                                            >
                                                {updatingPayment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                                                {updatingPayment ? "Opening…" : "Update payment method"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCancelFromBilling}
                                                data-testid="rivalry-pro-clerk-billing-cancel-btn"
                                                className="brutal-btn-ghost text-sm"
                                            >
                                                Cancel Pro+ subscription
                                            </button>
                                        </div>
                                    </div>
                                </UserButton.UserProfilePage>
                            </UserButton>
                        </div>
                    )}
                </div>
                {/* iter87.188 → iter87.189 — tagline stays; the previous
                    "How it works" + "Report an issue" header pills were
                    moved to the floating action button (bottom-right)
                    per operator on 2026-02-10. */}
                <div className="max-w-5xl mx-auto px-4 pb-3">
                    <p className="text-xs text-zinc-400">
                        Every crew you run or play in — one place.
                    </p>
                    {/* iter87.204 — Compact billing status, visible without
                        opening the Clerk account menu. Replaces the old
                        bare "Pro+"/"Tester" pill, which carried no useful
                        renewal/cancellation info. */}
                    {billingStatusLine && (
                        <p
                            className={`text-[11px] font-semibold mt-1 ${
                                me?.latest_subscription?.cancel_pending ? "text-amber-400"
                                    : me?.latest_subscription?.status === "suspended" ? "text-red-400"
                                    : "text-cyber"
                            }`}
                            data-testid="rivalry-pro-billing-status-banner"
                        >
                            {billingStatusLine}
                        </p>
                    )}
                </div>
            </header>

            <main className="max-w-5xl mx-auto p-4 space-y-6">
                {/* iter87.199.137 — Pro+ subscribe banner (Paddle). Only
                    renders for users whose effective tier is still `free`;
                    grandfathered / paid / comp users see the tier chip
                    in the header and no banner. */}
                {tier === "free" && <SubscribeProPlus />}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rivalry-pro-actions">
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => navigate("/rivalry-pro/new")}
                            data-testid="rivalry-pro-start-btn"
                            className="brutal-card w-full p-5 hover:border-cyber/80 hover:bg-cyber/5 transition-colors text-left group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 flex items-center justify-center bg-cyber/10 border border-cyber/40 rounded">
                                    <Plus className="w-5 h-5 text-cyber" />
                                </div>
                                <div className="flex-1 pr-6">
                                    <h2 className="font-display font-black text-base uppercase tracking-tight">
                                        Start a Rivalry
                                    </h2>
                                    <p className="text-[11px] text-zinc-500 mt-0.5">
                                        Create a new league you'll administrate
                                    </p>
                                </div>
                            </div>
                        </button>
                        {/* iter87.188 — SectionInfoDot lives OUTSIDE the tile <button>
                            (nested buttons are invalid HTML). Floated absolutely into
                            the top-right corner so it visually reads as part of the tile. */}
                        <div className="absolute top-3 right-3">
                            <SectionInfoDot
                                testid="rivalry-pro-info-start"
                                label="Start a Rivalry"
                                body="Create a brand-new crew you'll run. You'll be the admin — set the tournament, invite your people, and you're off."
                            />
                        </div>
                    </div>
                    {/* iter87.188 — "Claim a Rivalry" tile RETIRED. Every rivalry linked
                        to the Pro+ email surfaces automatically via case-insensitive
                        email matching; the only remaining "orphan adoption" edge case is
                        a site-admin function (compliance-driven data-deletion recovery).
                        Backend POST /rivalry-pro/claims now returns 410 Gone. */}
                </div>

                <section data-testid="rivalry-pro-list">
                    <div className="flex items-baseline gap-2 mb-3">
                        <h2 className="font-display font-black text-lg tracking-tight uppercase">
                            Your Rivalries
                        </h2>
                        <span className="font-display font-black text-base text-zinc-400" data-testid="rivalry-pro-count">
                            ({adminLeagues.length})
                        </span>
                        <SectionInfoDot
                            testid="rivalry-pro-info-your-rivalries"
                            label="Your Rivalries"
                            body="Crews you administrate. Every rivalry where the admin email matches your Pro+ address surfaces here automatically."
                        />
                        {/* iter87.212.1 — Manual refresh. Fixes reported P0: newly
                            added games/rivalries only showed up after a full
                            logout/login before this. Deliberately manual-only
                            (no auto-refresh/polling) per operator's call. */}
                        <button
                            type="button"
                            onClick={handleManualRefresh}
                            disabled={refreshing}
                            data-testid="rivalry-pro-refresh-btn"
                            title="Pull in any new rivalries or games right now"
                            className="brutal-btn-ghost text-[11px] py-1 px-2 ml-auto disabled:opacity-40 flex items-center gap-1"
                        >
                            <RotateCcw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                            {refreshing ? "Refreshing…" : "Refresh"}
                        </button>
                    </div>
                    {/* iter87.427 (user-requested) — To-do email reminders toggle,
                        right under the "Your Rivalries" header row (was below
                        the whole section). Plain right-justified text, not a
                        tile/card. */}
                    <div
                        className="flex items-center justify-end gap-2 mb-3"
                        data-testid="rivalry-pro-todo-reminder-toggle"
                    >
                        <span className="text-xs text-zinc-400">To-do email reminders</span>
                        <SectionInfoDot
                            testid="rivalry-pro-info-todo-reminder"
                            label="To-do email reminders"
                            body="On sends you one email a day if you have any to-dos outstanding across your rivalries — nothing outstanding means nothing lands in your inbox. Off and you won't get these."
                        />
                        <Switch
                            checked={!!me?.todo_reminder_opt_in}
                            onCheckedChange={handleToggleTodoReminder}
                            disabled={savingReminderPref}
                            data-testid="rivalry-pro-todo-reminder-switch"
                            aria-label="Daily to-do email reminders"
                            className="data-[state=unchecked]:bg-zinc-700 data-[state=unchecked]:border-zinc-500"
                        />
                    </div>
                    {adminLeagues.length === 0 ? (
                        <div className="brutal-card p-8 text-center border-dashed">
                            <p className="text-sm text-zinc-400 mb-2">No rivalries yet.</p>
                            <p className="text-xs text-zinc-500">
                                Click <strong className="text-cyber">Start a Rivalry</strong> above to create your first.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2" data-testid="rivalry-pro-admin-list">
                            {adminLeagues.map((l) => (
                                <LeagueRow key={l.id} league={l} kind="admin" todos={todosByLeague[l.id]} />
                            ))}
                        </div>
                    )}
                </section>

                {participantLeagues.length > 0 && (
                    <section data-testid="rivalry-pro-participant-list">
                        <div className="flex items-baseline gap-2 mb-3">
                            <h2 className="font-display font-black text-lg tracking-tight uppercase text-zinc-400">
                                Rivalries You Play In
                            </h2>
                            <span className="font-display font-black text-base text-zinc-500">
                                ({participantLeagues.length})
                            </span>
                            <SectionInfoDot
                                testid="rivalry-pro-info-play-in"
                                label="Rivalries You Play In"
                                body="Crews you're a player in but don't run. Auto-appears when any admin adds your Pro+ email to their roster."
                            />
                        </div>
                        <div className="space-y-2">
                            {/* iter87.422 (user-requested) — live/active
                                rivalries you play in surface ABOVE closed
                                ones in this same bucket (closed
                                participant leagues deliberately stay
                                here rather than moving to the admin-only
                                "Closed & Archived" section below). */}
                            {[...participantLeagues]
                                .sort((a, b) => (!!a.closed_at === !!b.closed_at) ? 0 : (a.closed_at ? 1 : -1))
                                .map((l) => (
                                    <LeagueRow key={l.id} league={l} kind="participant" todos={todosByLeague[l.id]} />
                                ))}
                        </div>
                    </section>
                )}

                {(adminClosedLeagues.length > 0 || adminArchivedLeagues.length > 0) && (
                    <section data-testid="rivalry-pro-archived-list">
                        {/* iter87.410 (user-reported) — was styled as a
                            `brutal-card-light` box, making it visually
                            indistinguishable from an actual rivalry row.
                            Restyled to match the plain-text section-header
                            pattern "Rivalries You Play In" uses above (same
                            flex row, same text-lg size, no card background/
                            border) so it unmistakably reads as a SECTION,
                            not a rivalry — the chevron is the only hint
                            it's collapsible. The toggle <button> only wraps
                            chevron+title+count (not the whole row) so the
                            info dot's Popover trigger can sit alongside it
                            as a sibling, same as every other section
                            header, instead of needing its own row below. */}
                        <div className="flex items-baseline gap-2 mb-3">
                            <button
                                type="button"
                                onClick={() => setArchivedOpen((v) => !v)}
                                data-testid="rivalry-pro-archived-toggle"
                                aria-expanded={archivedOpen}
                                className="flex items-baseline gap-2 text-left group"
                            >
                                {archivedOpen ? (
                                    <ChevronDown className="w-4 h-4 text-zinc-500 self-center shrink-0" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-zinc-500 self-center shrink-0" />
                                )}
                                <h2 className="font-display font-black text-lg tracking-tight uppercase text-zinc-400 group-hover:text-zinc-300 transition-colors">
                                    Closed &amp; Archived Rivalries
                                </h2>
                                <span
                                    className="font-display font-black text-base text-zinc-500"
                                    data-testid="rivalry-pro-archived-count"
                                >
                                    ({adminClosedLeagues.length + adminArchivedLeagues.length})
                                </span>
                            </button>
                            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                                read-only
                            </span>
                            <SectionInfoDot
                                testid="rivalry-pro-info-archived"
                                label="Closed & Archived Rivalries"
                                body="Closed rivalries are read-only but still viewable — hit Reopen from Rivalry Settings anytime. Archived rivalries are done for good; to reopen one, ask a site admin."
                            />
                        </div>
                        {archivedOpen && (
                            <div className="space-y-2" data-testid="rivalry-pro-archived-items">
                                {adminClosedLeagues.map((l) => (
                                    <LeagueRow key={l.id} league={l} kind="closed" />
                                ))}
                                {adminArchivedLeagues.map((l) => (
                                    <LeagueRow key={l.id} league={l} kind="archived" />
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </main>

            {/* iter87.199.69 — StartModal retired. Pro+ "Start a
                Rivalry" now navigates to `/rivalry-pro/new`. */}
            <HowItWorksSheet open={showHowItWorks} onClose={() => setShowHowItWorks(false)} />
            <ReportIssueModal
                open={showReportIssue}
                onClose={() => setShowReportIssue(false)}
                leagueId={null}
                apiClient={proApi}
            />
            <CancelProPlusModal
                open={showCancelProPlus}
                onClose={() => setShowCancelProPlus(false)}
                onCancelled={refreshMe}
            />
            {/* iter87.189 — Floating action button (bottom-right) that
                consolidates How-it-works + Report-an-issue + Start-a-
                rivalry + Cancel-Pro+. Start tile also stays visible at
                the top of the page (per operator: keep both entry
                points). */}
            <ProShellFab
                onAction={(key) => {
                    if (key === "start") navigate("/rivalry-pro/new");
                    else if (key === "howto") setShowHowItWorks(true);
                    else if (key === "report") setShowReportIssue(true);
                    else if (key === "cancel") setShowCancelProPlus(true);
                }}
            />
            <AddToHomeScreenBanner />
        </div>
    );
}

function LeagueRow({ league, kind = "admin", todos = null }) {
    const navigate = useNavigate();
    const isParticipant = kind === "participant";
    const isClosed = kind === "closed";
    const isArchived = kind === "archived";
    // iter87.422 (user-reported) — a participant-only league stays
    // closed-but-viewable in the SAME "Rivalries You Play In" bucket
    // (see routes/pro_plus/user.py's my_leagues docstring), so detect
    // it here via the `closed_at` field the backend now stamps on
    // participant items too.
    const isParticipantClosed = isParticipant && !!league.closed_at;
    const [opening, setOpening] = useState(false);
    // iter87.199.125 — `active_tournaments` is now `{ open, live, total }`.
    // `open` = active but not currently live (not started, or finished
    // but the admin hasn't closed it yet). `live` = a game is actually
    // being played right now.
    const tournamentSplit = league.active_tournaments;
    const openTournaments = typeof tournamentSplit === "object" && tournamentSplit
        ? (tournamentSplit.open ?? 0)
        : (tournamentSplit ?? 0);
    const liveTournaments = typeof tournamentSplit === "object" && tournamentSplit
        ? (tournamentSplit.live ?? 0)
        : 0;
    // iter87.199.7 — Placeholder-claim prompt state. Populated when the
    // backend `openLeagueSession` returns `needs_username_pick: true`
    // for a Pro+ user who was added by an admin as a placeholder
    // Participant. The modal collects a username, calls
    // `claim_placeholder`, then navigates.
    const [claimPrompt, setClaimPrompt] = useState(null);

    const openLeagueTo = async ({ admin = false, tournamentId = null } = {}) => {
        if (opening) return;
        setOpening(true);
        try {
            // iter87.79-c — session bridge: Pro+ owner might not have a
            // legacy participant session for this league (auto-surfaced by
            // admin_email match only). Ask backend to backfill + hand us
            // a session shape we can write to `pickem_session_v1`.
            const s = await proApi.openLeagueSession(league.id);
            // iter87.199.7 — Placeholder-claim prompt. If the caller is
            // a placeholder Participant (admin-added by email, never
            // claimed), backend returns `needs_username_pick` instead
            // of a session. Show the claim modal; on success it
            // saves + navigates.
            if (s?.needs_username_pick && s?.claim_prompt) {
                setClaimPrompt(s.claim_prompt);
                return;
            }
            legacySession.save({
                league: { id: s.league_id, name: league.name, invite_code: league.invite_code || null },
                participant: {
                    id: s.participant_id,
                    username: s.username,
                    email: null,
                    is_admin: s.is_admin,
                },
            });
            // iter87.394 (user-requested) — To-do deep links: as a
            // player, drop into the right TOURNAMENT (same client-side
            // "view tournament" mechanism the dashboard's own switcher
            // uses); as admin, jump straight into Admin scoped to that
            // tournament via the query param AdminPanel already reads.
            if (admin) {
                navigate(`/league/${league.id}/admin${tournamentId ? `?tournament_id=${tournamentId}` : ""}`);
            } else {
                if (tournamentId) setStoredViewTournament(league.id, tournamentId);
                navigate(`/league/${league.id}`);
            }
        } catch (err) {
            const msg = err.response?.data?.detail || "Unable to open rivalry";
            toast.error(typeof msg === "string" ? msg : "Unable to open rivalry");
        } finally {
            setOpening(false);
        }
    };
    const openLeague = () => openLeagueTo({});

    return (
        <div
            className={`brutal-card-light p-4 flex flex-wrap items-center gap-3 ${isParticipant || isClosed || isArchived ? "opacity-80" : ""}`}
            data-testid={`rivalry-pro-league-${league.id}`}
        >
            <Trophy className={`w-4 h-4 ${isParticipant || isClosed || isArchived ? "text-zinc-500" : "text-cyber"}`} />
            <div className="flex-1 min-w-[180px]">
                <p className="font-display font-extrabold text-sm flex items-center gap-2">
                    {league.name}
                    {isParticipant && (
                        <span
                            className="pill text-[9px] font-bold uppercase border-zinc-700 text-zinc-500"
                            data-testid={`rivalry-pro-participant-badge-${league.id}`}
                        >
                            Player
                        </span>
                    )}
                </p>
                <p className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                    <Users className="w-3 h-3" />
                    {league.member_count ?? 0} members
                    {!isClosed && !isArchived && !isParticipantClosed && (
                        <>
                            <span>·</span>
                            <span data-testid={`rivalry-pro-tournament-split-${league.id}`}>
                                {openTournaments} open{liveTournaments > 0 ? ` · ${liveTournaments} live` : ""}
                            </span>
                        </>
                    )}
                </p>
            </div>
            {/* iter87.435 (user-requested) — Unified "Closed" pill: a
                closed rivalry (whether you admin it or just play in it)
                now gets the exact same red, tappable "Closed" pill on
                the right of the card — no more separate inline "Closed"
                label + standalone "View" button. "Archived" has no
                click target (not meaningfully viewable) but moves to
                this same right-aligned slot instead of sitting inline
                next to the title. */}
            {isClosed ? (
                <RivalryTodoBadge
                    leagueId={league.id}
                    busy={opening}
                    closed
                    onOpenRivalry={openLeague}
                />
            ) : isArchived ? (
                <span
                    className="pill text-[9px] font-bold uppercase border-zinc-700 text-zinc-500"
                    data-testid={`rivalry-pro-archived-badge-${league.id}`}
                >
                    Archived
                </span>
            ) : (
                <RivalryTodoBadge
                    leagueId={league.id}
                    todos={todos}
                    busy={opening}
                    closed={isParticipantClosed}
                    onOpenRivalry={openLeague}
                    onOpenTodo={(item, kind) => openLeagueTo({ admin: kind === "admin", tournamentId: item.tournament_id || null })}
                />
            )}
            {/* iter87.395 (user-requested) — Standalone "Open" button
                removed: the to-do badge now covers both cases — the
                zero-state "All caught up" pill opens the rivalry
                directly, and a populated badge's popover rows deep-link
                into the right spot. Archived rows already hid the badge
                above, so they now have no click target at all (archived
                rivalries aren't meaningfully viewable anyway). */}
            {/* iter87.199.7 — Placeholder-claim modal. Fires when the
                caller is a placeholder Participant (admin-added by
                email) and this is their first Pro+ hub open. */}
            <ProPlusClaimModal
                open={!!claimPrompt}
                prompt={claimPrompt}
                leagueId={league.id}
                leagueName={league.name}
                inviteCode={league.invite_code}
                onClose={() => setClaimPrompt(null)}
                onClaimed={(s) => {
                    setClaimPrompt(null);
                    navigate(`/league/${s.league_id}`);
                }}
            />
        </div>
    );
}

// iter87.199.69 — StartModal deleted. Pro+ "Start a Rivalry" now
// routes to `/rivalry-pro/new` and uses the shared linear onboarding
// flow (`<Start mode="pro_plus" />`). Consent (T&C + 18+) is captured
// on Step 0 of that flow, giving every new Pro+ Rivalry a symmetric
// audit stamp with Free-tier creates.

// iter87.188 — ClaimModal deleted (endpoint retired to 410 Gone).

function ReleaseModal() { return null; } // iter87.115 amended — ReleaseModal removed. Kept as stub to survive stale hot-reload references; delete next iter.

// Keep the symbol exported so hot-reloaded bundles that still reference
// it get a no-op instead of a hard crash. Safe to delete after one push.
export { ReleaseModal as __ReleaseModal_stub };
